import React from 'react';
import CostCenter from '../../../components/CostCenter';
import { Supplier, UserProfile, FinancialConfig, Material, ProductTemplate, SimpleService } from '../../../types';
import { ProfileFinancialState } from './ProfileEditorModal';

export interface CostCenterHybridProps {
  financialState: ProfileFinancialState;
  onUpdateFinancialState: (updated: ProfileFinancialState) => Promise<void> | void;
  onOpenSuppliers?: () => void;
  onAutoSaveSupplier?: (supplier: Supplier) => void;
  onBack: () => void;
}

/**
 * Wrapper de Demonstração / Lab-ERP para Centro de Custos.
 * Renderiza o componente canônico CostCenter com tipagem estrita via React.ComponentProps.
 */
export default function CostCenterHybrid({
  financialState,
  onUpdateFinancialState,
  onOpenSuppliers,
  onAutoSaveSupplier,
  onBack
}: CostCenterHybridProps) {
  // Trava de tipagem estrita com o componente real de produção
  type EnforcedCostCenterProps = React.ComponentProps<typeof CostCenter>;

  const mockUser: UserProfile = {
    id: 'demo-user-costcenter',
    ownerName: 'Gestor Modelo',
    businessName: 'Empresa Modelo Lab-ERP',
    email: 'lab@resolvmax.com.br',
    niche: 'geral',
    plan: 'Empresa',
    isTrialActive: true,
    maxFreeQuotes: 99999,
  };

  const mockConfig: FinancialConfig = {
    userId: 'demo-user-costcenter',
    monthlyFixedCost: (financialState.fixedCosts || []).reduce((acc, c) => acc + (c.monthlyValue || 0), 0) || 3500,
    proLaboreGoal: financialState.desiredProLabore || 5000,
    weeklyWorkHours: (financialState.workHoursPerDay || 8) * (financialState.workDaysPerWeek || 5),
    baseHourValue: 65,
    paymentFeePercentage: 3.5,
    defaultCommissionPercentage: 0,
  };

  const [materials, setMaterials] = React.useState<Material[]>([
    { userId: 'demo-user-costcenter', id: 'm1', name: 'Cabo Flexível 2,5mm² (Rolo 100m)', unit: 'rolo', pricePaid: 185.00, yieldAmount: 100, category: 'variável' },
    { userId: 'demo-user-costcenter', id: 'm2', name: 'Disjuntor Bipolar 40A Curva C', unit: 'un', pricePaid: 38.00, yieldAmount: 1, category: 'variável' },
  ]);

  const [recipes, setRecipes] = React.useState<ProductTemplate[]>([]);
  const [services, setServices] = React.useState<SimpleService[]>([]);

  const realProps: EnforcedCostCenterProps = {
    user: mockUser,
    config: mockConfig,
    materials,
    recipes,
    services,
    onSaveMaterials: (mats) => setMaterials(mats),
    onSaveRecipe: (recipe) => setRecipes((prev) => [...prev.filter((r) => r.id !== recipe.id), recipe]),
    onDeleteRecipe: (id) => setRecipes((prev) => prev.filter((r) => r.id !== id)),
    onSaveService: (srv) => setServices((prev) => [...prev.filter((s) => s.id !== srv.id), srv]),
    onDeleteService: (id) => setServices((prev) => prev.filter((s) => s.id !== id)),
    onBack,
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm overflow-y-auto">
      <CostCenter {...realProps} />
    </div>
  );
}
